import { t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import type { RevisionMissingResult } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { FileSearch } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'

import { InlineTurnCard } from './docChatStyles'

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const RevisionList = styled.ul`
  margin: 0;
  padding-left: 1.125rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`

const FooterRow = styled.div`
  display: flex;
  justify-content: flex-start;
`

interface DocChatRevisionMissingCardProps {
  result: RevisionMissingResult
  /** True once the jump target row has hydrated; false hides the open button. */
  canOpen: boolean
  onOpen: () => void
}

/** Why a "nothing missing" claim cannot be made. Empty when nothing is unsure. */
function incompleteReasons(result: RevisionMissingResult): string[] {
  const reasons: string[] = []
  if (result.findings_unavailable === true) {
    reasons.push(t`Findings are unavailable for this drawing.`)
  }
  if (result.inventory_in_scope === false) {
    reasons.push(t`The document inventory is not fully in scope.`)
  }
  if (result.table_scan_complete === false) {
    reasons.push(t`The revision table scan is incomplete.`)
  }
  if (result.inventory_search_complete === false) {
    reasons.push(t`The inventory search is incomplete.`)
  }
  const unreadable = result.unreadable_member_count ?? null
  if (unreadable !== null && unreadable > 0) {
    reasons.push(t`${unreadable} documents could not be read.`)
  }
  return reasons
}

function countLine(
  count: number | null | undefined,
  known: (value: number) => string,
  unknown: string
): string {
  return count === null || count === undefined ? unknown : known(count)
}

function sheetText(result: RevisionMissingResult): string | null {
  const drawing = result.drawing_number ?? null
  const sheet = result.sheet_number ?? null
  if (drawing !== null && sheet !== null)
    return t`Drawing ${drawing}, sheet ${sheet}`
  if (drawing !== null) return t`Drawing ${drawing}`
  return null
}

export function DocChatRevisionMissingCard({
  result,
  canOpen,
  onOpen,
}: DocChatRevisionMissingCardProps): React.ReactElement {
  useLingui()
  const reasons = incompleteReasons(result)
  const sheet = sheetText(result)
  const auditState = result.audit_state
  const showNoneMissing = result.nothing_missing_confirmed
  const needsReviewCount = result.needs_review_count ?? null
  const hasUnknownCoverage = !result.audit_complete
  const missingLines = result.missing_revisions
    .map((revision) =>
      [revision.label, revision.date, revision.description]
        .filter((part): part is string => typeof part === 'string')
        .join(' · ')
    )
    .filter((line) => line.length > 0)

  return (
    <InlineTurnCard>
      <CardHeader>
        <FileSearch aria-hidden />
        <Text as='p' variant='label' weight='medium'>
          <Trans>Revision audit</Trans>
        </Text>
        {sheet ? (
          <Text as='p' variant='caption' color='muted'>
            {sheet}
          </Text>
        ) : null}
      </CardHeader>
      {auditState ? (
        <Text as='p' variant='caption' color='muted'>
          {t`Audit state: ${auditState}`}
        </Text>
      ) : null}
      {showNoneMissing ? (
        <Text as='p' variant='body'>
          <Trans>No missing revisions found in a complete audit.</Trans>
        </Text>
      ) : null}
      {hasUnknownCoverage ? (
        <Text as='p' variant='body'>
          <Trans>
            This audit is not complete, so missing revisions cannot be ruled
            out.
          </Trans>
        </Text>
      ) : null}
      {reasons.map((reason) => (
        <Text key={reason} as='p' variant='caption' color='muted'>
          {reason}
        </Text>
      ))}
      <Text as='p' variant='caption' color='muted'>
        {[
          countLine(
            result.missing_count,
            (count) => t`${count} missing`,
            t`Missing count unknown`
          ),
          countLine(
            result.available_count,
            (count) => t`${count} available`,
            t`Available count unknown`
          ),
        ].join(' · ')}
      </Text>
      {needsReviewCount === null || needsReviewCount > 0 ? (
        <Text as='p' variant='caption' color='muted'>
          {countLine(
            needsReviewCount,
            (count) => t`${count} need review (not counted as missing)`,
            t`Needs-review count unknown (not counted as missing)`
          )}
        </Text>
      ) : null}
      {missingLines.length > 0 ? (
        <RevisionList>
          {missingLines.map((line, index) => (
            <li key={`${line}-${index}`}>
              <Text as='span' variant='caption'>
                {line}
              </Text>
            </li>
          ))}
        </RevisionList>
      ) : null}
      {result.jump_ref ? (
        <FooterRow>
          <Button
            type='button'
            variant='outline'
            size='sm'
            disabled={!canOpen}
            onClick={onOpen}>
            <Trans>Open document</Trans>
          </Button>
        </FooterRow>
      ) : null}
    </InlineTurnCard>
  )
}
